/**
 * pages/ReviewerDashboard.jsx
 * Professional redesign — enhanced stat cards, Recharts donut, completion ring,
 * filter bar, and polished table rows. All data-fetching logic unchanged.
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  PieChart, Pie, Cell, Tooltip as RCTooltip,
  ResponsiveContainer, Legend,
} from 'recharts';
import API from '../services/api';
import ReviewPage from './ReviewPage';
import LifecycleModal from '../components/LifecycleModal';
import { LifecyclePanel, BatchLifecyclePanel } from './LifecyclePage';

/* ── Global CSS (injected once) ─────────────────────────────────── */
let _cssInjected = false;
function injectCSS() {
  if (_cssInjected) return;
  const el = document.createElement('style');
  el.textContent = `
    @keyframes rd-shimmer {
      0%   { background-position: -600px 0; }
      100% { background-position:  600px 0; }
    }
    .rd-skel {
      background: linear-gradient(90deg, #F0EBE5 25%, #E8E2DB 50%, #F0EBE5 75%);
      background-size: 600px 100%;
      animation: rd-shimmer 1.5s infinite linear;
      border-radius: 6px;
    }
    @keyframes rd-pulse-ring {
      0%   { box-shadow: 0 0 0 0 rgba(217,119,6,0.4); }
      70%  { box-shadow: 0 0 0 8px rgba(217,119,6,0); }
      100% { box-shadow: 0 0 0 0 rgba(217,119,6,0); }
    }
    .rd-pulse { animation: rd-pulse-ring 2s infinite; }
    @keyframes rd-row-in {
      from { opacity: 0; transform: translateY(6px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    .rd-row { animation: rd-row-in 0.25s ease both; }
    .rd-tr:hover td { background: #FFF8F2 !important; }
    .rd-tr-urgent { box-shadow: inset 3px 0 0 #D97706; }
    .rd-action-primary {
      padding: 6px 16px; border-radius: 8px; border: none;
      background: linear-gradient(135deg,#F97316,#EA580C);
      color: #fff; font-size: 0.8rem; font-weight: 700;
      cursor: pointer; font-family: inherit; white-space: nowrap;
      transition: opacity 0.15s, transform 0.1s;
    }
    .rd-action-primary:hover { opacity: 0.88; transform: translateY(-1px); }
    .rd-action-ghost {
      padding: 5px 12px; border-radius: 8px; border: 1px solid #E5E0DA;
      background: #fff; color: #5C5450; font-size: 0.8rem; font-weight: 600;
      cursor: pointer; font-family: inherit; white-space: nowrap;
      transition: background 0.15s;
    }
    .rd-action-ghost:hover { background: #F5F0EA; }
    .rd-icon-btn {
      padding: 5px 8px; border-radius: 7px; border: 1px solid #E5E0DA;
      background: #FAFAF8; color: #8C7E77; font-size: 0.72rem; font-weight: 600;
      cursor: pointer; font-family: inherit; display: flex; align-items: center; gap: 4px;
      transition: background 0.15s;
    }
    .rd-icon-btn:hover { background: #F0EBE3; }
    .rd-filter-tab {
      padding: 6px 14px; border-radius: 20px; border: 1.5px solid transparent;
      background: none; font-size: 0.8rem; font-weight: 600; cursor: pointer;
      font-family: inherit; color: #6B6360; transition: all 0.15s;
      display: flex; align-items: center; gap: 6px;
    }
    .rd-filter-tab:hover { background: #F5F0EA; }
    .rd-filter-tab-active {
      background: #FFF7ED; border-color: #F97316; color: #C2410C;
    }
    .rd-search-input {
      padding: 7px 12px 7px 36px; border-radius: 9px; border: 1.5px solid #E5E0DA;
      background: #FAFAF8; font-size: 0.83rem; color: #1A1410; font-family: inherit;
      outline: none; transition: border-color 0.15s, box-shadow 0.15s; width: 200px;
    }
    .rd-search-input:focus { border-color: #F97316; box-shadow: 0 0 0 3px rgba(249,115,22,0.1); }
    @keyframes rd-spin {
      from { transform: rotate(0deg); }
      to   { transform: rotate(360deg); }
    }
    .rd-spin { animation: rd-spin 0.7s linear infinite; display: inline-block; }
    .rd-refresh-btn {
      display: flex; align-items: center; gap: 7px;
      padding: 8px 16px; border-radius: 9px;
      border: 1.5px solid #E5E0DA; background: #fff;
      font-size: 0.82rem; color: #5C5450; cursor: pointer; font-family: inherit;
      transition: border-color 0.15s, box-shadow 0.15s;
    }
    .rd-refresh-btn:hover:not(:disabled) { border-color: #F97316; color: #C2410C; }
    .rd-refresh-btn:disabled { opacity: 0.7; cursor: default; }
  `;
  document.head.appendChild(el);
  _cssInjected = true;
}
injectCSS();

/* ── Skeleton helpers ────────────────────────────────────────────── */
function Skel({ w = '100%', h = 14, r = 6, style = {} }) {
  return <div className="rd-skel" style={{ width: w, height: h, borderRadius: r, flexShrink: 0, ...style }} />;
}
function StatCardSkeleton() {
  return (
    <div style={{ flex: 1, background: '#fff', borderRadius: 14, padding: '20px', border: '1px solid #EDE8E1', display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <Skel w={44} h={36} r={6} />
          <Skel w={110} h={10} r={4} />
        </div>
        <Skel w={40} h={40} r={10} />
      </div>
    </div>
  );
}
function TableRowsSkeleton() {
  const WIDTHS = [180, 110, 90, 90, 72, 80];
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr>
          {['Drawing', 'Shared By', 'Shared On', 'Reviewed', 'Status', 'Action'].map(h => (
            <th key={h} style={ST.th}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {Array.from({ length: 6 }).map((_, ri) => (
          <tr key={ri} style={{ borderBottom: '1px solid #F2EDE8' }}>
            {WIDTHS.map((w, ci) => (
              <td key={ci} style={{ padding: '14px 16px', verticalAlign: 'middle' }}>
                <Skel w={w} h={ci === 4 ? 22 : 13} r={ci === 4 ? 12 : 5} />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/* ── UTC parse helper — backend sends naive UTC strings without 'Z' ── */
const toUTC = iso => iso ? new Date(iso.endsWith('Z') || iso.includes('+') ? iso : iso + 'Z') : null;

/* ── Helpers ─────────────────────────────────────────────────────── */
function fmtTime(iso) {
  if (!iso) return '—';
  return toUTC(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}
function fmtFullDate(d) {
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
}
function getGreeting(name) {
  const h = new Date().getHours();
  const part = h < 12 ? 'morning' : h < 17 ? 'afternoon' : 'evening';
  return `Good ${part}, ${name ?? 'Reviewer'}`;
}
function dayAge(iso) {
  if (!iso) return 0;
  return Math.floor((Date.now() - toUTC(iso).getTime()) / 86_400_000);
}
function fmtAge(days) {
  if (days === 0) return 'Today';
  if (days === 1) return '1 day ago';
  return `${days} days ago`;
}

function StatusBadge({ status }) {
  const map = {
    pending:       { label: 'Pending',       bg: '#FFF7ED', color: '#C2410C', dot: '#D97706' },
    approved:      { label: 'Approved',      bg: '#ECFDF5', color: '#065F46', dot: '#059669' },
    rejected:      { label: 'Rejected',      bg: '#FEF2F2', color: '#991B1B', dot: '#DC2626' },
    self_approved: { label: 'Self Approved', bg: '#F0FDF4', color: '#166534', dot: '#059669' },
  };
  const cfg = map[status] || { label: status, bg: '#F2EDE8', color: '#5C5450', dot: '#9C9490' };
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 10px', borderRadius: 20, fontSize: '0.72rem', fontWeight: 700, backgroundColor: cfg.bg, color: cfg.color, whiteSpace: 'nowrap' }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: cfg.dot, flexShrink: 0 }} />
      {cfg.label}
    </span>
  );
}

function IconLogo() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 3H5a2 2 0 0 0-2 2v4m6-6h10a2 2 0 0 1 2 2v4M9 3v18m0 0h10a2 2 0 0 0 2-2V9M9 21H5a2 2 0 0 1-2-2V9m0 0h18"/>
    </svg>
  );
}

/* ── Stat Card ───────────────────────────────────────────────────── */
function StatCard({ label, value, accent, icon, pulse, sub }) {
  return (
    <div style={{
      flex: 1, background: '#fff', borderRadius: 14, padding: '18px 20px',
      border: '1px solid #EDE8E1', borderLeft: `4px solid ${accent}`,
      display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
      boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
    }}>
      <div>
        <div style={{ fontSize: '2rem', fontWeight: 800, color: accent, lineHeight: 1 }}>{value}</div>
        <div style={{ fontSize: '0.8rem', color: '#5C5450', marginTop: 5, fontWeight: 500 }}>{label}</div>
        {sub && <div style={{ fontSize: '0.72rem', color: '#9C9490', marginTop: 3 }}>{sub}</div>}
      </div>
      <div className={pulse && value > 0 ? 'rd-pulse' : ''} style={{
        width: 42, height: 42, borderRadius: 11,
        background: `${accent}18`, border: `1px solid ${accent}30`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: accent, flexShrink: 0,
      }}>
        {icon}
      </div>
    </div>
  );
}

/* ── SVG Completion Ring ─────────────────────────────────────────── */
function CompletionRing({ pct, reviewed, total, oldestPendingDays, avgTurnaround }) {
  const R = 52, C = 2 * Math.PI * R;
  const dash = C * (pct / 100);
  return (
    <div style={{ background: '#fff', borderRadius: 16, padding: '20px 24px', border: '1px solid #EDE8E1', flex: 1, display: 'flex', flexDirection: 'column', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
      <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#1A1410', marginBottom: 4 }}>Completion Rate</div>
      <div style={{ fontSize: '0.78rem', color: '#9C9490', marginBottom: 16 }}>How much of your queue is cleared</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
        <div style={{ position: 'relative', flexShrink: 0 }}>
          <svg width="124" height="124" viewBox="0 0 124 124">
            {/* rotate -90° around center so arc starts at 12 o'clock */}
            <g transform="rotate(-90 62 62)">
              <circle cx="62" cy="62" r={R} fill="none" stroke="#F0EBE5" strokeWidth="10" />
              <circle
                cx="62" cy="62" r={R} fill="none"
                stroke={pct >= 80 ? '#059669' : pct >= 40 ? '#D97706' : '#F97316'}
                strokeWidth="10" strokeLinecap="round"
                strokeDasharray={`${dash} ${C - dash}`}
                style={{ transition: 'stroke-dasharray 0.8s ease' }}
              />
            </g>
          </svg>
          <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ fontSize: '1.5rem', fontWeight: 800, color: '#1A1410', lineHeight: 1 }}>{pct}%</span>
            <span style={{ fontSize: '0.65rem', color: '#9C9490', marginTop: 2 }}>complete</span>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, flex: 1 }}>
          <StatLine label="Reviewed" value={`${reviewed} / ${total}`} color="#059669" />
          <StatLine label="Oldest pending" value={oldestPendingDays != null ? fmtAge(oldestPendingDays) : '—'} color="#D97706" />
          <StatLine label="Avg turnaround" value={avgTurnaround != null ? `${avgTurnaround}d` : '—'} color="#6366F1" />
        </div>
      </div>
    </div>
  );
}
function StatLine({ label, value, color }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <span style={{ fontSize: '0.7rem', color: '#9C9490', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>{label}</span>
      <span style={{ fontSize: '0.9rem', fontWeight: 700, color }}>{value}</span>
    </div>
  );
}

/* ── Custom Recharts tooltip ─────────────────────────────────────── */
function DonutTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const { name, value, payload: { fill } } = payload[0];
  const pct = payload[0].payload.pct;
  return (
    <div style={{ background: '#1A1410', borderRadius: 9, padding: '8px 14px', fontSize: '0.82rem', color: '#fff', boxShadow: '0 4px 16px rgba(0,0,0,0.18)' }}>
      <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: fill, marginRight: 6 }} />
      <strong>{name}</strong>: {value} ({pct}%)
    </div>
  );
}

/* ── ReviewerLifecycleView (unchanged) ───────────────────────────── */
function ReviewerLifecycleView({ reviews }) {
  const [selectedId,      setSelectedId]      = useState(null);
  const [selectedBatchId, setSelectedBatchId] = useState(null);
  const [expandedBatches, setExpandedBatches] = useState(new Set());

  const toggleBatch = id =>
    setExpandedBatches(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  return (
    <div style={{ flex: 1, display: 'flex', overflow: 'hidden', background: '#FAFAF9' }}>
      <aside style={{ width: 260, flexShrink: 0, borderRight: '1px solid #EDE8E1', background: '#fff', overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '16px 14px 12px', borderBottom: '1px solid #EDE8E1', fontWeight: 700, fontSize: 14, color: '#2C2825', position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
          Lifecycle Tracker
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
          {reviews.length === 0 && (
            <div style={{ padding: 16, color: '#9C9490', fontSize: 13, textAlign: 'center' }}>No reviews found.</div>
          )}
          {reviews.map(rr => {
            if (rr.is_batch) {
              const isOpen      = expandedBatches.has(rr.review_id);
              const batchActive = selectedBatchId === rr.batch_id && !selectedId;
              return (
                <div key={rr.review_id}>
                  <div style={{ display: 'flex', alignItems: 'center', padding: '10px 14px', borderBottom: '1px solid #F5F0EA', background: batchActive ? '#FFF7ED' : '#FFFBF5', borderLeft: batchActive ? '3px solid #D97706' : '3px solid transparent' }}>
                    <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#B45309', fontSize: 12, padding: '0 6px 0 0', flexShrink: 0 }}
                      onClick={e => { e.stopPropagation(); toggleBatch(rr.review_id); }}>{isOpen ? '▼' : '▶'}</button>
                    <button style={{ background: 'none', border: 'none', cursor: 'pointer', flex: 1, textAlign: 'left', padding: 0 }}
                      onClick={() => { setSelectedBatchId(rr.batch_id); setSelectedId(null); }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: '#2C2825' }}>{rr.drawing_name || `Batch (${rr.file_count} files)`}</div>
                    </button>
                    <StatusBadge status={rr.status} />
                  </div>
                  {isOpen && (rr.files || []).map(f => {
                    const active = selectedId === f.id;
                    return (
                      <button key={f.id}
                        style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 14px 8px 30px', background: active ? '#FFF7ED' : '#FDFAF6', border: 'none', borderBottom: '1px solid #F5F0EA', cursor: 'pointer', borderLeft: active ? '3px solid #D97706' : '3px solid transparent', boxSizing: 'border-box' }}
                        onClick={() => { setSelectedId(f.id); setSelectedBatchId(null); }}>
                        <span style={{ color: '#B45309', fontSize: 10, marginRight: 4 }}>└</span>
                        <span style={{ fontSize: 12, fontWeight: 600, color: '#2C2825' }}>{f.display_name || f.file_name}</span>
                      </button>
                    );
                  })}
                </div>
              );
            }
            const active = selectedId === rr.drawing_id;
            return (
              <button key={rr.review_id}
                style={{ display: 'block', width: '100%', textAlign: 'left', padding: '10px 14px', background: active ? '#FFF7ED' : 'none', border: 'none', borderBottom: '1px solid #F5F0EA', cursor: 'pointer', borderLeft: active ? '3px solid #D97706' : '3px solid transparent', boxSizing: 'border-box' }}
                onClick={() => { setSelectedId(rr.drawing_id); setSelectedBatchId(null); }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#2C2825', marginBottom: 4 }}>{rr.drawing_name}</div>
                <StatusBadge status={rr.status} />
              </button>
            );
          })}
        </div>
      </aside>
      <main style={{ flex: 1, overflowY: 'auto', padding: '24px 28px' }}>
        {!selectedId && !selectedBatchId ? (
          <div style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#D1CBC4" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
            </svg>
            <div style={{ marginTop: 12, color: '#9C9490', fontSize: 14 }}>Select a drawing or batch on the left to view its lifecycle.</div>
          </div>
        ) : selectedBatchId ? (
          <BatchLifecyclePanel batchId={selectedBatchId} />
        ) : (
          <LifecyclePanel drawingId={selectedId} />
        )}
      </main>
    </div>
  );
}

/* ── Delete confirm modal ────────────────────────────────────────── */
function DeleteConfirmModal({ reviewName, onConfirm, onCancel, busy }) {
  return (
    <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(24,18,14,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 24 }}>
      <div style={{ backgroundColor: '#fff', borderRadius: 14, padding: 24, width: '100%', maxWidth: 400, boxShadow: '0 16px 48px rgba(24,18,14,0.2)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <div style={{ width: 36, height: 36, borderRadius: 10, background: '#FEF2F2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/>
            </svg>
          </div>
          <h3 style={{ fontSize: '1rem', fontWeight: 700, color: '#1A1410', margin: 0 }}>Delete Review</h3>
        </div>
        <p style={{ fontSize: '0.87rem', color: '#5C5450', margin: '0 0 8px', lineHeight: 1.5 }}>
          Are you sure you want to delete the review for <strong style={{ color: '#1A1410' }}>{reviewName}</strong>?
        </p>
        <p style={{ fontSize: '0.82rem', color: '#B45309', background: '#FFF7ED', border: '1px solid #FED7AA', borderRadius: 8, padding: '8px 12px', margin: '0 0 20px', lineHeight: 1.5 }}>
          The uploader will be notified in the lifecycle log that this review was deleted.
        </p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button onClick={onCancel} disabled={busy} style={{ padding: '9px 20px', borderRadius: 9, border: '1.5px solid #E5E0DA', background: '#F5F0EA', color: '#5C5450', fontWeight: 600, cursor: busy ? 'not-allowed' : 'pointer', fontFamily: 'inherit', fontSize: '0.87rem' }}>
            Cancel
          </button>
          <button onClick={onConfirm} disabled={busy} style={{ padding: '9px 20px', borderRadius: 9, border: 'none', background: busy ? '#FCA5A5' : '#DC2626', color: '#fff', fontWeight: 700, cursor: busy ? 'not-allowed' : 'pointer', fontFamily: 'inherit', fontSize: '0.87rem', minWidth: 90, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
            {busy ? <span style={{ width: 14, height: 14, border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#fff', borderRadius: '50%', display: 'inline-block', animation: 'rd-spin 0.7s linear infinite' }} /> : null}
            {busy ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Main Component ──────────────────────────────────────────────── */
export default function ReviewerDashboard({ authUser, onSignOut, onAuthSuccess }) {
  const [reviews,          setReviews]          = useState(() => API.getCached('reviewer_dashboard') ?? []);
  const [loading,          setLoading]          = useState(() => !API.getCached('reviewer_dashboard'));
  const [error,            setError]            = useState(null);
  const [activeToken,      setActiveToken]      = useState(null);
  const [expandedBatches,  setExpandedBatches]  = useState(new Set());
  const [reviewerView,     setReviewerView]     = useState('reviews');
  const [lifecycleDrawing, setLifecycleDrawing] = useState(null);
  const [lastRefreshed,    setLastRefreshed]    = useState(null);
  const [refreshing,       setRefreshing]       = useState(false);
  const [searchQuery,      setSearchQuery]      = useState('');
  const [statusFilter,     setStatusFilter]     = useState('all');
  const [page,             setPage]             = useState(1);
  const [deleteModal,      setDeleteModal]      = useState(null); // { reviewId, reviewName }
  const [deleteBusy,       setDeleteBusy]       = useState(false);

  const PAGE_SIZE = 10;

  const load = useCallback(async (isManual = false) => {
    try {
      setError(null);
      if (isManual) setRefreshing(true);
      const data = await API.getReviewerDashboard();
      setReviews(Array.isArray(data) ? data : []);
      setLastRefreshed(new Date());
    } catch (err) {
      setError(err.error || 'Failed to load reviews.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  const reload = useCallback(() => {
    API.invalidateCache('reviewer_dashboard');
    load(true);
  }, [load]);

  useEffect(() => {
    load();
    const id = setInterval(load, 60_000);
    return () => clearInterval(id);
  }, [load]);

  const handleReviewClick = (token) => setActiveToken(token);

  const handleDeleteConfirm = async () => {
    if (!deleteModal) return;
    setDeleteBusy(true);
    try {
      await API.deleteReview(deleteModal.reviewId);
      setReviews(prev => prev.filter(r => r.review_id !== deleteModal.reviewId));
      setDeleteModal(null);
    } catch (err) {
      setError(err.error || 'Failed to delete review. Please try again.');
      setDeleteModal(null);
    } finally {
      setDeleteBusy(false);
    }
  };

  const toggleBatchRow = (reviewId) => {
    setExpandedBatches(prev => {
      const next = new Set(prev);
      next.has(reviewId) ? next.delete(reviewId) : next.add(reviewId);
      return next;
    });
  };

  /* ── Derived stats ── */
  const pending  = reviews.filter(r => r.status === 'pending').length;
  const approved = reviews.filter(r => r.status === 'approved' || r.status === 'self_approved').length;
  const rejected = reviews.filter(r => r.status === 'rejected').length;
  const reviewed = approved + rejected;
  const total    = reviews.length;
  const pct      = total > 0 ? Math.round((reviewed / total) * 100) : 0;

  const pendingItems = reviews.filter(r => r.status === 'pending');
  const oldestPendingDays = pendingItems.length > 0
    ? Math.max(...pendingItems.map(r => dayAge(r.shared_at)))
    : null;

  const reviewedItems = reviews.filter(r => r.reviewed_at && r.shared_at);
  const avgTurnaround = reviewedItems.length > 0
    ? Math.round(reviewedItems.reduce((sum, r) => sum + dayAge(r.shared_at) - dayAge(r.reviewed_at), 0) / reviewedItems.length * 10) / 10
    : null;

  /* ── Donut chart data ── */
  const chartData = [
    { name: 'Pending',  value: pending,  pct: total > 0 ? Math.round(pending/total*100)  : 0, fill: '#D97706' },
    { name: 'Approved', value: approved, pct: total > 0 ? Math.round(approved/total*100) : 0, fill: '#059669' },
    { name: 'Rejected', value: rejected, pct: total > 0 ? Math.round(rejected/total*100) : 0, fill: '#DC2626' },
  ].filter(d => d.value > 0);
  const CHART_EMPTY = [{ name: 'No data', value: 1, pct: 0, fill: '#EDE8E1' }];

  /* ── Filtered reviews ── */
  const filtered = reviews.filter(rr => {
    const matchStatus = statusFilter === 'all' || rr.status === statusFilter;
    const q = searchQuery.toLowerCase();
    const matchSearch = !q || rr.drawing_name?.toLowerCase().includes(q) || rr.uploader_name?.toLowerCase().includes(q);
    return matchStatus && matchSearch;
  });

  /* ── Pagination ── */
  const totalPages  = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage    = Math.min(page, totalPages);
  const pageStart   = (safePage - 1) * PAGE_SIZE;
  const paginated   = filtered.slice(pageStart, pageStart + PAGE_SIZE);

  /* Reset to page 1 whenever filter/search changes */
  const setFilter = (val) => { setStatusFilter(val); setPage(1); };
  const setSearch = (val) => { setSearchQuery(val);  setPage(1); };

  /* ── Last refreshed label ── */
  const refreshLabel = lastRefreshed
    ? (() => {
        const s = Math.floor((Date.now() - lastRefreshed.getTime()) / 1000);
        if (s < 10)  return 'just now';
        if (s < 60)  return `${s}s ago`;
        if (s < 120) return '1 min ago';
        return `${Math.floor(s/60)} min ago`;
      })()
    : '';

  /* ── Sidebar ── */
  const sidebar = (
    <aside style={ST.sidebar}>
      <div style={ST.brandRow}>
        <div style={ST.brandIcon}><IconLogo /></div>
        <div>
          <div style={ST.brandTitle}>InSpectly.AI</div>
          <div style={ST.brandSub}>Engineering Intelligence</div>
        </div>
      </div>
      <div style={ST.roleChip}><span style={ST.roleChipDot} />Reviewer Access</div>
      <div style={ST.navLabel}>Reviews</div>
      <div style={{ ...ST.navItem, ...(!activeToken && reviewerView === 'reviews' ? ST.navItemActive : {}), cursor: 'pointer' }}
        onClick={() => { setActiveToken(null); setReviewerView('reviews'); reload(); }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
        </svg>
        My Reviews
        {pending > 0 && (
          <span style={{ marginLeft: 'auto', background: '#F97316', color: '#fff', borderRadius: 10, fontSize: '0.68rem', fontWeight: 700, padding: '1px 7px' }}>{pending}</span>
        )}
      </div>
      <div style={{ ...ST.navItem, ...(!activeToken && reviewerView === 'lifecycle' ? ST.navItemActive : {}), cursor: 'pointer' }}
        onClick={() => { setReviewerView('lifecycle'); setActiveToken(null); }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
        </svg>
        Lifecycle
      </div>
      {activeToken && (
        <div style={{ ...ST.navItem, ...ST.navItemActive }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
          </svg>
          Review Scan
        </div>
      )}
      <div style={ST.sidebarFooter}>
        <div style={ST.userRow}>
          <div style={ST.avatar}>{authUser?.username?.[0]?.toUpperCase() ?? 'R'}</div>
          <div style={ST.userInfo}>
            <span style={ST.userName}>{authUser?.username}</span>
            <span style={ST.userEmail}>{authUser?.email}</span>
          </div>
          <button style={ST.signOutBtn} onClick={onSignOut} title="Sign out">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
              <polyline points="16 17 21 12 16 7"/>
              <line x1="21" y1="12" x2="9" y2="12"/>
            </svg>
          </button>
        </div>
      </div>
    </aside>
  );

  /* ── Reviewing inline ── */
  if (activeToken) {
    return (
      <div style={ST.root}>
        {sidebar}
        <main style={{ ...ST.main, padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <div style={{ flex: 1, overflowY: 'auto', position: 'relative' }}>
            <ReviewPage
              token={activeToken}
              authUser={authUser}
              onAuthSuccess={onAuthSuccess || (() => {})}
              onBackToDashboard={() => { setActiveToken(null); reload(); }}
            />
          </div>
        </main>
      </div>
    );
  }

  if (reviewerView === 'lifecycle' && !activeToken) {
    return (
      <div style={ST.root}>
        {sidebar}
        <ReviewerLifecycleView reviews={reviews} />
      </div>
    );
  }

  /* ── Main dashboard ── */
  return (
    <div style={ST.root}>
      {sidebar}
      {/* main is a pure scroll shell; the inner div is the flex-column layout container
          so its children grow to natural height instead of being flex-shrunk to fit 100vh */}
      <main style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', backgroundColor: ST.root.backgroundColor }}>
      <div style={{ padding: '20px 28px', display: 'flex', flexDirection: 'column', gap: 18 }}>

        {/* ── Header ── */}
        <header style={ST.header}>
          <div style={{ background: 'linear-gradient(135deg,#FFF7ED 0%,#FFF3E5 100%)', borderRadius: 14, padding: '16px 22px', border: '1px solid #FDE8C8', flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
              <div>
                <h1 style={ST.pageTitle}>{getGreeting(authUser?.username)}</h1>
                <p style={ST.pageSub}>{fmtFullDate(new Date())} &nbsp;·&nbsp; Review and approve inspection plans</p>
              </div>
              <button className="rd-refresh-btn" onClick={reload} disabled={refreshing}>
                <span className={refreshing ? 'rd-spin' : ''} style={{ display: 'flex', alignItems: 'center' }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/>
                    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
                  </svg>
                </span>
                {refreshing ? 'Refreshing…' : <>Refresh {refreshLabel && <span style={{ color: '#9C9490', fontWeight: 400, fontSize: '0.75rem' }}>· {refreshLabel}</span>}</>}
              </button>
            </div>
          </div>
        </header>

        {/* ── Error ── */}
        {error && (
          <div style={ST.errorBox}>
            <span style={ST.errorDot}>!</span> {error}
          </div>
        )}

        {/* ── Stat cards row ── */}
        <div style={ST.statsRow}>
          {loading ? (
            <><StatCardSkeleton /><StatCardSkeleton /><StatCardSkeleton /><StatCardSkeleton /></>
          ) : (
            <>
              <StatCard
                label="Total Assigned" value={total} accent="#F97316"
                sub={`${pending} awaiting action`}
                icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>}
              />
              <StatCard
                label="Pending Review" value={pending} accent="#D97706" pulse
                sub={pending > 0 ? `oldest: ${fmtAge(oldestPendingDays ?? 0)}` : 'all clear'}
                icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>}
              />
              <StatCard
                label="Approved" value={approved} accent="#059669"
                sub={total > 0 ? `${Math.round(approved/total*100)}% of total` : '—'}
                icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>}
              />
              <StatCard
                label="Rejected" value={rejected} accent="#DC2626"
                sub={total > 0 ? `${Math.round(rejected/total*100)}% of total` : '—'}
                icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>}
              />
            </>
          )}
        </div>

        {/* ── Overview row: Donut + Completion Ring ── */}
        {!loading && total > 0 && (
          <div style={ST.overviewRow}>
            {/* Donut chart */}
            <div style={{ background: '#fff', borderRadius: 16, padding: '20px 24px', border: '1px solid #EDE8E1', flex: 1.2, display: 'flex', flexDirection: 'column', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
              <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#1A1410', marginBottom: 4 }}>Review Breakdown</div>
              <div style={{ fontSize: '0.78rem', color: '#9C9490', marginBottom: 12 }}>Distribution across all statuses</div>
              <div style={{ flex: 1, minHeight: 180 }}>
                <ResponsiveContainer width="100%" height={180}>
                  <PieChart>
                    <Pie
                      data={chartData.length ? chartData : CHART_EMPTY}
                      cx="50%" cy="50%"
                      innerRadius={52} outerRadius={75}
                      paddingAngle={chartData.length > 1 ? 3 : 0}
                      dataKey="value"
                      startAngle={90} endAngle={-270}
                      isAnimationActive
                    >
                      {(chartData.length ? chartData : CHART_EMPTY).map((entry, i) => (
                        <Cell key={i} fill={entry.fill} stroke="none" />
                      ))}
                    </Pie>
                    {chartData.length > 0 && <RCTooltip content={<DonutTooltip />} />}
                    <Legend
                      iconType="circle" iconSize={8}
                      formatter={(value, entry) => (
                        <span style={{ color: '#1A1410', fontSize: '0.8rem', fontWeight: 600 }}>
                          {value} <span style={{ color: '#9C9490', fontWeight: 400 }}>({entry.payload.value})</span>
                        </span>
                      )}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Completion ring */}
            <CompletionRing
              pct={pct}
              reviewed={reviewed}
              total={total}
              oldestPendingDays={oldestPendingDays}
              avgTurnaround={avgTurnaround}
            />
          </div>
        )}

        {/* ── Table card ── */}
        <div style={ST.card}>
          {/* Card header with search + filter tabs */}
          <div style={{ padding: '16px 20px', borderBottom: '1px solid #EDE8E1' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginBottom: 12 }}>
              <div>
                <h2 style={ST.cardTitle}>Assigned Reviews</h2>
                {loading
                  ? <Skel w={60} h={10} r={4} style={{ marginTop: 4 }} />
                  : <p style={ST.cardSub}>{filtered.length} of {total} shown</p>
                }
              </div>
              {/* Search */}
              <div style={{ position: 'relative' }}>
                <svg style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}
                  width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9C9490" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
                <input
                  className="rd-search-input"
                  type="text"
                  placeholder="Search drawing or uploader…"
                  value={searchQuery}
                  onChange={e => setSearch(e.target.value)}
                />
              </div>
            </div>
            {/* Status filter tabs */}
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {[
                { key: 'all',      label: 'All',      count: total    },
                { key: 'pending',  label: 'Pending',  count: pending  },
                { key: 'approved', label: 'Approved', count: approved },
                { key: 'rejected', label: 'Rejected', count: rejected },
              ].map(tab => (
                <button
                  key={tab.key}
                  className={`rd-filter-tab${statusFilter === tab.key ? ' rd-filter-tab-active' : ''}`}
                  onClick={() => setFilter(tab.key)}
                >
                  {tab.label}
                  <span style={{
                    background: statusFilter === tab.key ? '#F97316' : '#EDE8E1',
                    color: statusFilter === tab.key ? '#fff' : '#5C5450',
                    borderRadius: 10, fontSize: '0.7rem', fontWeight: 700, padding: '1px 7px',
                    transition: 'all 0.15s',
                  }}>{tab.count}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Table body */}
          {loading ? (
            <div style={{ overflowX: 'auto' }}><TableRowsSkeleton /></div>
          ) : filtered.length === 0 ? (
            <div style={ST.emptyBox}>
              <div style={ST.emptyIcon}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#D4C8BE" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
                </svg>
              </div>
              <p style={ST.emptyText}>{searchQuery || statusFilter !== 'all' ? 'No reviews match your filter.' : 'No reviews assigned yet.'}</p>
              <p style={ST.emptySub}>{searchQuery || statusFilter !== 'all' ? 'Try clearing your search or changing the status filter.' : 'When an Uploader shares a drawing with your email, it will appear here.'}</p>
            </div>
          ) : (
            <div style={ST.tableWrap}>
              <table style={ST.table}>
                <thead>
                  <tr>
                    <th style={ST.th}>Drawing</th>
                    <th style={ST.th}>Shared By</th>
                    <th style={ST.th}>Shared On</th>
                    <th style={ST.th}>Reviewed</th>
                    <th style={ST.th}>Status</th>
                    <th style={ST.th}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {paginated.map((rr, ri) => {
                    const isBatch    = rr.is_batch;
                    const isExpanded = expandedBatches.has(rr.review_id);
                    const age        = rr.status === 'pending' ? dayAge(rr.shared_at) : 0;
                    const isUrgent   = age >= 3;

                    return [
                      <tr key={rr.review_id}
                        className={`rd-tr rd-row${isUrgent ? ' rd-tr-urgent' : ''}`}
                        style={{ borderBottom: '1px solid #F2EDE8', animationDelay: `${ri * 0.04}s`, ...(isBatch ? { background: '#FFFDF9' } : {}) }}
                      >
                        {/* Drawing name */}
                        <td style={ST.td}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            {/* File type icon */}
                            <div style={{
                              width: 34, height: 34, borderRadius: 8, flexShrink: 0,
                              background: isBatch ? '#FFF7ED' : '#F0F4FF',
                              border: `1px solid ${isBatch ? '#FDE8C8' : '#DBEAFE'}`,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                              {isBatch ? (
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#D97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
                                  <rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
                                </svg>
                              ) : (
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#4F6EF7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>
                                </svg>
                              )}
                            </div>
                            <div>
                              <div style={{ fontWeight: 600, fontSize: '0.88rem', color: '#1A1410', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {rr.drawing_name}
                              </div>
                              {isBatch ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 3 }}>
                                  <span style={{ fontSize: '0.7rem', background: '#FFF7ED', color: '#B45309', border: '1px solid #FDE8C8', borderRadius: 5, padding: '1px 6px', fontWeight: 700 }}>
                                    Batch · {rr.file_count} file{rr.file_count !== 1 ? 's' : ''}
                                  </span>
                                  <button
                                    onClick={() => toggleBatchRow(rr.review_id)}
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#B45309', fontSize: 11, padding: 0, fontWeight: 700 }}
                                  >
                                    {isExpanded ? '▲ collapse' : '▼ expand'}
                                  </button>
                                </div>
                              ) : (
                                isUrgent && (
                                  <div style={{ fontSize: '0.7rem', color: '#B45309', marginTop: 2, display: 'flex', alignItems: 'center', gap: 4 }}>
                                    <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#D97706', display: 'inline-block' }} />
                                    {fmtAge(age)} · needs review
                                  </div>
                                )
                              )}
                            </div>
                          </div>
                        </td>

                        {/* Shared By */}
                        <td style={{ ...ST.td, ...ST.tdMuted }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                            <div style={{ width: 24, height: 24, borderRadius: '50%', background: '#F0EBE4', color: '#7C6E67', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.65rem', fontWeight: 700, flexShrink: 0 }}>
                              {rr.uploader_name?.[0]?.toUpperCase() ?? '?'}
                            </div>
                            <span style={{ fontSize: '0.83rem' }}>{rr.uploader_name}</span>
                          </div>
                        </td>

                        {/* Shared On */}
                        <td style={{ ...ST.td, ...ST.tdMuted, fontSize: '0.82rem' }}>{fmtTime(rr.shared_at)}</td>

                        {/* Reviewed At */}
                        <td style={{ ...ST.td, ...ST.tdMuted, fontSize: '0.82rem' }}>
                          {rr.reviewed_at ? fmtTime(rr.reviewed_at) : (
                            <span style={{ color: '#D97706', fontWeight: 600, fontSize: '0.78rem' }}>Awaiting</span>
                          )}
                        </td>

                        {/* Status */}
                        <td style={ST.td}><StatusBadge status={rr.status} /></td>

                        {/* Action */}
                        <td style={ST.td}>
                          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                            {rr.token_expired ? (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 12px', borderRadius: 8, fontSize: '0.78rem', fontWeight: 700, background: '#F3F3F2', color: '#9C9490', border: '1px solid #E5E0DA', whiteSpace: 'nowrap' }}>
                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                  <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                                </svg>
                                Link Expired
                              </span>
                            ) : (
                              rr.share_token && (
                                <button
                                  className={rr.status === 'pending' ? 'rd-action-primary' : 'rd-action-ghost'}
                                  onClick={() => handleReviewClick(rr.share_token)}
                                >
                                  {rr.status === 'pending' ? (isBatch ? 'Review Batch →' : 'Review →') : 'View'}
                                </button>
                              )
                            )}
                            {!isBatch && rr.drawing_id && (
                              <button
                                className="rd-icon-btn"
                                onClick={() => setLifecycleDrawing({ drawingId: rr.drawing_id, drawingName: rr.drawing_name })}
                                title="View lifecycle"
                              >
                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                  <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
                                </svg>
                                Lifecycle
                              </button>
                            )}
                            <button
                              className="rd-icon-btn"
                              onClick={() => setDeleteModal({ reviewId: rr.review_id, reviewName: rr.drawing_name })}
                              title="Delete this review"
                              style={{ color: '#DC2626', borderColor: '#FECACA' }}
                            >
                              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/>
                              </svg>
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>,

                      /* Expanded batch files */
                      ...(isBatch && isExpanded ? [
                        <tr key={`${rr.review_id}-files`}>
                          <td colSpan={6} style={{ padding: '8px 20px 14px 64px', background: '#FDF8F2' }}>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                              {(rr.files || []).map((f, fi) => {
                                const dColor = f.decision === 'approved' ? '#065F46' : f.decision === 'rejected' ? '#991B1B' : '#7C6E67';
                                const dBg    = f.decision === 'approved' ? '#ECFDF5' : f.decision === 'rejected' ? '#FEF2F2' : '#F5F0EA';
                                return (
                                  <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '5px 12px', borderRadius: 8, background: '#fff', border: '1px solid #E8E2DA', fontSize: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                                    <span style={{ color: '#B45309', fontWeight: 700, fontSize: 11 }}>#{fi + 1}</span>
                                    <span style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 500, color: '#1A1410' }}>{f.display_name || f.file_name}</span>
                                    <span style={{ padding: '2px 8px', borderRadius: 12, fontSize: 10, fontWeight: 700, background: dBg, color: dColor, whiteSpace: 'nowrap' }}>
                                      {f.decision === 'pending' ? 'Pending' : f.decision}
                                    </span>
                                    <button
                                      className="rd-icon-btn"
                                      style={{ padding: '2px 7px', fontSize: 10 }}
                                      onClick={() => setLifecycleDrawing({ drawingId: f.id, drawingName: f.display_name || f.file_name })}
                                    >
                                      Lifecycle
                                    </button>
                                  </div>
                                );
                              })}
                            </div>
                          </td>
                        </tr>,
                      ] : []),
                    ];
                  })}
                </tbody>
              </table>

              {/* ── Pagination bar ── */}
              {totalPages > 1 && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 20px', borderTop: '1px solid #EDE8E1', background: '#FAFAF8' }}>
                  <span style={{ fontSize: '0.8rem', color: '#9C9490' }}>
                    Showing <strong style={{ color: '#1A1410' }}>{pageStart + 1}–{Math.min(pageStart + PAGE_SIZE, filtered.length)}</strong> of <strong style={{ color: '#1A1410' }}>{filtered.length}</strong> reviews
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    {/* Prev */}
                    <button
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={safePage === 1}
                      style={{
                        padding: '5px 12px', borderRadius: 8, border: '1.5px solid #E5E0DA',
                        background: safePage === 1 ? '#F5F2EE' : '#fff',
                        color: safePage === 1 ? '#C4BDB8' : '#5C5450',
                        fontSize: '0.8rem', fontWeight: 600, cursor: safePage === 1 ? 'default' : 'pointer',
                        fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 5,
                      }}
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
                      Prev
                    </button>

                    {/* Page number pills */}
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter(n => n === 1 || n === totalPages || Math.abs(n - safePage) <= 1)
                      .reduce((acc, n, idx, arr) => {
                        if (idx > 0 && n - arr[idx - 1] > 1) acc.push('…');
                        acc.push(n);
                        return acc;
                      }, [])
                      .map((item, idx) =>
                        item === '…' ? (
                          <span key={`ellipsis-${idx}`} style={{ padding: '0 4px', color: '#9C9490', fontSize: '0.8rem' }}>…</span>
                        ) : (
                          <button
                            key={item}
                            onClick={() => setPage(item)}
                            style={{
                              width: 32, height: 32, borderRadius: 8,
                              border: item === safePage ? 'none' : '1.5px solid #E5E0DA',
                              background: item === safePage ? '#F97316' : '#fff',
                              color: item === safePage ? '#fff' : '#5C5450',
                              fontSize: '0.8rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
                            }}
                          >
                            {item}
                          </button>
                        )
                      )
                    }

                    {/* Next */}
                    <button
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={safePage === totalPages}
                      style={{
                        padding: '5px 12px', borderRadius: 8, border: '1.5px solid #E5E0DA',
                        background: safePage === totalPages ? '#F5F2EE' : '#fff',
                        color: safePage === totalPages ? '#C4BDB8' : '#5C5450',
                        fontSize: '0.8rem', fontWeight: 600, cursor: safePage === totalPages ? 'default' : 'pointer',
                        fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 5,
                      }}
                    >
                      Next
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>{/* end inner layout div */}
      </main>

      {/* Lifecycle modal */}
      {lifecycleDrawing && (
        <LifecycleModal
          drawingId={lifecycleDrawing.drawingId}
          drawingName={lifecycleDrawing.drawingName}
          onClose={() => setLifecycleDrawing(null)}
        />
      )}

      {/* Delete confirm modal */}
      {deleteModal && (
        <DeleteConfirmModal
          reviewName={deleteModal.reviewName}
          onConfirm={handleDeleteConfirm}
          onCancel={() => !deleteBusy && setDeleteModal(null)}
          busy={deleteBusy}
        />
      )}
    </div>
  );
}

/* ── Styles ──────────────────────────────────────────────────────── */
const ST = {
  root:   { display: 'flex', height: '100vh', overflow: 'hidden', fontFamily: "'Inter','Segoe UI',sans-serif", backgroundColor: '#F5F2EE' },

  sidebar: { width: 240, flexShrink: 0, minHeight: 0, backgroundColor: '#18120E', display: 'flex', flexDirection: 'column', padding: '20px 0', overflowY: 'auto' },
  brandRow:  { display: 'flex', alignItems: 'center', gap: 10, padding: '0 20px 16px' },
  brandIcon: { width: 36, height: 36, borderRadius: 9, backgroundColor: 'rgba(249,115,22,0.18)', border: '1px solid rgba(249,115,22,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#F97316', flexShrink: 0 },
  brandTitle:{ color: '#FFFFFF', fontSize: '0.95rem', fontWeight: 700 },
  brandSub:  { color: '#6B5F57', fontSize: '0.72rem' },
  roleChip:  { display: 'flex', alignItems: 'center', gap: 6, margin: '0 16px 16px', padding: '5px 10px', backgroundColor: 'rgba(5,150,105,0.15)', border: '1px solid rgba(5,150,105,0.3)', borderRadius: 6, fontSize: '0.73rem', color: '#34D399', fontWeight: 600 },
  roleChipDot: { width: 6, height: 6, borderRadius: '50%', backgroundColor: '#34D399', flexShrink: 0 },
  navLabel:    { color: '#4E4744', fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.08em', padding: '0 20px 8px', textTransform: 'uppercase' },
  navItem:     { display: 'flex', alignItems: 'center', gap: 10, padding: '9px 20px', fontSize: '0.85rem', color: '#9C9490', cursor: 'pointer', userSelect: 'none' },
  navItemActive:{ backgroundColor: 'rgba(249,115,22,0.12)', color: '#F97316', borderRight: '2px solid #F97316' },
  sidebarFooter:{ marginTop: 'auto', padding: '16px 12px 0', borderTop: '1px solid rgba(255,255,255,0.05)' },
  userRow:     { display: 'flex', alignItems: 'center', gap: 8 },
  avatar:      { width: 30, height: 30, borderRadius: '50%', backgroundColor: '#F97316', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 700, flexShrink: 0 },
  userInfo:    { flex: 1, minWidth: 0 },
  userName:    { display: 'block', color: '#F5F0EB', fontSize: '0.82rem', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  userEmail:   { display: 'block', color: '#6B5F57', fontSize: '0.72rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  signOutBtn:  { background: 'none', border: 'none', cursor: 'pointer', color: '#6B5F57', padding: 4, display: 'flex' },

  main: { flex: 1, minHeight: 0, overflowY: 'auto', padding: '20px 28px', display: 'flex', flexDirection: 'column', gap: 18 },

  header:    { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' },
  pageTitle: { fontSize: '1.35rem', fontWeight: 800, color: '#1A1410', margin: '0 0 4px' },
  pageSub:   { fontSize: '0.82rem', color: '#7C6460', margin: 0 },

  statsRow:  { display: 'flex', gap: 14 },
  overviewRow: { display: 'flex', gap: 14 },

  errorBox:  { display: 'flex', alignItems: 'center', gap: 10, backgroundColor: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: 10, padding: '12px 16px', color: '#DC2626', fontSize: '0.85rem' },
  errorDot:  { width: 20, height: 20, borderRadius: '50%', backgroundColor: '#DC2626', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 700, flexShrink: 0 },

  card:       { backgroundColor: '#FFFFFF', borderRadius: 16, border: '1px solid #EDE8E1', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' },
  cardTitle:  { fontSize: '1rem', fontWeight: 700, color: '#1A1410', margin: '0 0 2px' },
  cardSub:    { fontSize: '0.78rem', color: '#9C9490', margin: 0 },

  emptyBox:   { padding: '52px 20px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 },
  emptyIcon:  { width: 60, height: 60, borderRadius: 16, backgroundColor: '#F7F4F0', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  emptyText:  { fontSize: '0.95rem', fontWeight: 600, color: '#1A1410', margin: 0 },
  emptySub:   { fontSize: '0.82rem', color: '#9C9490', margin: 0, maxWidth: 340 },

  tableWrap: { overflowX: 'auto' },
  table:     { width: '100%', borderCollapse: 'collapse' },
  th:        { padding: '10px 16px', textAlign: 'left', fontSize: '0.72rem', fontWeight: 700, color: '#9C9490', backgroundColor: '#FAFAF8', borderBottom: '1px solid #EDE8E1', whiteSpace: 'nowrap', position: 'sticky', top: 0, zIndex: 1, textTransform: 'uppercase', letterSpacing: '0.05em' },
  td:        { padding: '12px 16px', fontSize: '0.85rem', color: '#1A1410', verticalAlign: 'middle' },
  tdMuted:   { color: '#5C5450' },
};
